import isMobileDevice from 'ismobilejs'
import { Dispatch, SetStateAction } from 'react'

import { SseReference } from './sse-v2-types.ts'

export const MODELS = {
  'imk-apertus-1.5': 'Apertus 1.5 70B (Infomaniak)',
  'imk-apertus-70b': 'Apertus 1.0 70B (Infomaniak)',
  'imk-qwen3.5-122b': 'Qwen3.5 122B (Infomaniak)',
  'imk-mistral-4': 'Mistral Small 4 (Infomaniak)',
} as const

export type ModelId = keyof typeof MODELS

export const DEFAULT_MODEL: ModelId = 'imk-apertus-1.5'

export const isModelId = (value: string | null): value is ModelId => !!value && value in MODELS

export type UsedLinks = SseReference
//export const isMobile = true
export const isTouchDevice = window ? isMobileDevice(window?.navigator).any : false
export const isMobilePhone = window ? isMobileDevice(window?.navigator).phone : false

const linkImageGetHrefForImage = (node: HTMLElement) => {
  const a = node.parentNode?.querySelector('a')
  return a?.getAttribute('href')
}

export const linkImageonMouserOver = (e: React.MouseEvent<HTMLDivElement>) => {
  const node = e.target as HTMLElement

  if (node.nodeName === 'IMG') {
    const src = node.getAttribute('src')
    if (src && src.toLowerCase().includes('width-840')) {
      const parentWidth = node.parentElement?.offsetWidth || 840
      const imgWidth = node.offsetWidth + 1
      const maxScale = parentWidth / imgWidth
      const scale = Math.min(7, maxScale) // Use 3 or maxScale, whichever is smaller

      node.style.transform = `scale(${scale})`
    } else {
      node.style.transform = 'scale(3)'
    }
  }
}

export const linkImageOnMouseOut = (e: React.MouseEvent<HTMLDivElement>) => {
  const node = e.target as HTMLElement
  if (node.nodeName === 'IMG') {
    node.style.transform = 'scale(1)'
  }
}

export const linkImageOnMouseClick = (e: React.MouseEvent<HTMLDivElement>) => {
  const node = e.target as HTMLElement
  if (node.nodeName === 'IMG') {
    const href = linkImageGetHrefForImage(node)
    if (href) {
      window.open(href, '_blank')
    }
  }
}

export const replaceLinksWithCallback = (
  input: string,
  usedLinks: UsedLinks[],
  setResult: Dispatch<SetStateAction<string>>,
) => {
  const linkMatches = input.match(/\[\^([0-9]+(?:\.[0-9]+)*)\^*]/g)
  if (linkMatches) {
    setResult(replaceLinks(input, usedLinks))
  }
}

const FOOTNOTE = /\s*\[\^[\^0-9., ]+]/g
// a marker is either a plain number ("2") or a grouped one ("1.2"), where the
// leading part is the group — one batch of usedLinks — and the last part is the
// position within that group
const FOOTNOTE_NUMBER = /[0-9]+(?:\.[0-9]+)*/g

// usedLinks arrives in batches that get concatenated, so a group starts after all
// earlier ones and we need their sizes to resolve a marker to a flat index
const linkNumberOf = (groupSizes: number[]) => (marker: string) => {
  const [linkNumber, group] = marker
    .split('.')
    .reverse()
    .map(part => parseInt(part))
  // ungrouped marker, or stored history that doesn't carry the batch sizes: the
  // marker is a flat index, which is what a single group amounts to anyway
  if (group === undefined || groupSizes.length === 0) {
    return linkNumber
  }
  // batch not received (yet) — no link is better than one to the wrong source
  if (groupSizes[group - 1] === undefined) {
    return NaN
  }
  return groupSizes.slice(0, group - 1).reduce((offset, size) => offset + size, 0) + linkNumber
}
// official business designations, e.g. "STRB Nr. 2883/2024" – these become the link text themselves
const REFERENCE = /(?:STRB|GRB)\s+Nr\.\s*\d+\/\d{4}/

const linkTo = (linkNumber: number, url: string, text: string) => `[${text}](${url} "__link: ${linkNumber}")`

const replaceLinksInLine = (line: string, usedLinks: UsedLinks[], groupSizes: number[]) => {
  const linkNumbers = [
    ...new Set(
      (line.match(FOOTNOTE) || []).flatMap(fMatch => fMatch.match(FOOTNOTE_NUMBER) || []).map(linkNumberOf(groupSizes)),
    ),
  ].filter(linkNumber => usedLinks[linkNumber - 1])

  // the markers always go away, whether we have a link for them or not
  let newLine = line.replace(FOOTNOTE, '')

  const reference = newLine.match(REFERENCE)
  const [first, ...rest] = linkNumbers

  if (reference?.index !== undefined && first) {
    newLine =
      newLine.slice(0, reference.index) +
      linkTo(first, usedLinks[first - 1].url, reference[0]) +
      newLine.slice(reference.index + reference[0].length)
  }

  // anything we couldn't attach to a designation gets appended as before
  return (
    newLine +
    (reference && first ? rest : linkNumbers)
      .map(linkNumber => ` (${linkTo(linkNumber, usedLinks[linkNumber - 1].url, `Link ${linkNumber}`)})`)
      .join('')
  )
}

// groupSizes holds the length of each usedLinks batch, in arrival order
export const replaceLinks = (input: string, usedLinks: UsedLinks[], groupSizes: number[] = []) =>
  input.includes('[^')
    ? input
        .split('\n')
        .map(line => replaceLinksInLine(line, usedLinks, groupSizes))
        .join('\n')
    : input

export const showGeneratedSummary = (result: {
  description?: string
  summary?: string
  type?: string | null | undefined
}) => {
  if (!!result.summary && result.type === 'pdf') {
    return true
  }
  return (!result.description || result.description.length < 140) && !!result.summary
}

export function formatWithThousand(value: number): string {
  return Math.round(value).toLocaleString('de-CH').replace(/\./g, "'")
}
